<script setup lang="ts">
  import { ref, useTemplateRef } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useUser } from '@gat-ship/user'
  import { testIds, type TenantUser } from '@gat-ship/types'
  import {
    Button,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription,
    SearchInput
  } from '@gat-ship/vue-components'
  import { IconPlus } from '@gat-ship/icons'
  import {
    TenantUsersList,
    TenantUsersModalProvider,
    TenantUsersProvider
  } from '@gat-ship/tenant-users'
  import { useAdminTabClass } from '../../composables/useAdminTabClass'

  // The global AdministrationModal opens purely off `?admin=tenant-users` and is
  // mounted for every authenticated user, so this panel must gate itself — the
  // router TenantUsersView enforces the same check via PageLayout access-props.
  const user = useUser()

  // Local (non-deep-linked) view state, mirroring the routed view's Current /
  // Historical tabs and search. Kept off the URL so it doesn't clobber the
  // route view's own `?show` / `?search-filter` query params.
  const activeTab = ref<'active' | 'inactive'>('active')
  const search = ref('')

  // Add lives behind TenantUsersModalProvider; reach its showModal the same way
  // PanelPorts reaches PortsManager.openAdd — via the provider's exposed API.
  const modals =
    useTemplateRef<InstanceType<typeof TenantUsersModalProvider>>('modals')

  function openAdd() {
    modals.value?.showAddTenantUser()
  }

  const route = useRoute()
  const router = useRouter()

  // From the AdministrationModal panel, a row click opens the self-contained
  // tenant-user detail modal (driven by `?tenantUser` via the global
  // TenantUserDetailModalProvider), layered over the admin modal.
  function openUser(user: TenantUser) {
    router.push({
      query: {
        ...route.query,
        tenantUser: user.userId,
        tenantUserSection: 'general'
      }
    })
  }

  const tabClass = useAdminTabClass(activeTab, 'pill')
</script>

<template>
  <div
    v-if="!user.isTenantAdmin.value"
    class="text-muted-foreground p-10 text-center text-sm"
  >
    You don't have permission to manage tenant users.
  </div>
  <TenantUsersProvider v-else>
    <TenantUsersModalProvider ref="modals">
      <!-- pt-3 offsets the modal's -mt-3 under-fade pull; pb-5 keeps the last
           row off the modal edge (the ports/estimated-times panel precedent). -->
      <div class="flex flex-col gap-6 pb-5 pt-3">
        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle
              >Tenant Users
              <Button
                class="ml-auto shrink-0"
                :data-testid="testIds.tenantUser.actions.addTenantUser"
                @click="openAdd"
              >
                <IconPlus class="size-4" />
                Add Tenant User
              </Button></ModalSectionHeaderTitle
            >
            <ModalSectionHeaderDescription>
              View and manage all users in your tenant.
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <div class="flex items-center gap-2">
            <div class="bg-muted flex items-center gap-1 rounded-lg p-1">
              <button
                type="button"
                :class="tabClass('active')"
                :data-testid="testIds.tenantUser.table.views.active"
                @click="activeTab = 'active'"
              >
                Current
              </button>
              <button
                type="button"
                :class="tabClass('inactive')"
                :data-testid="testIds.tenantUser.table.views.inactive"
                @click="activeTab = 'inactive'"
              >
                Historical
              </button>
            </div>
            <SearchInput
              v-model="search"
              clearable
              class="ml-auto max-w-[250px]"
              placeholder="Search Filter..."
            />
          </div>
          <TenantUsersList
            :active-tab="activeTab"
            :search="search"
            @select="openUser"
          />
        </ModalSection>
      </div>
    </TenantUsersModalProvider>
  </TenantUsersProvider>
</template>
